import { useMemo, useState } from 'react';

import { AlertTriangle, FileText } from 'lucide-react';

import type { ItemDoChecklist } from '@/modulos/portal-credenciado/comum/contrato';
import { DocumentChecklist } from '@/modulos/portal-credenciado/comum/DocumentChecklist';
import { GatingBanner } from '@/modulos/portal-credenciado/comum/GatingBanner';
import { usePortalAccess } from '@/modulos/portal-credenciado/comum/usePortalAccess';
import { useAuth } from '@/nucleo/auth/useAuth';
import { veDocumentacaoPreCredenciamento } from '@/nucleo/navegacao/mapa';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { CardSkeleton } from '@/ui/base/table-skeleton';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';

import type { ItemDaApi } from './contrato';
import { DialogoDeEnvioDeDocumento } from './DialogoDeEnvioDeDocumento';
import { idDoItem, montarGrupos, paraOChecklist, textoDoResumo, useDocumentos } from './dados';

/**
 * `/credenciado/documentos` — os documentos que o edital exige da empresa, de
 * cada veículo e de cada condutor e monitor, com envio e reenvio. Dado real da
 * API `portal-credenciado-documentos` (0279).
 *
 * Composição do protótipo (`pages/portal/PortalDocumentos.tsx`) — cabeçalho,
 * aviso de pendência operacional e checklist — com um cartão por grupo, na
 * ordem Empresa, Veículos, Condutores, Monitores (decisão F1). Cartão com
 * documento pendente, rejeitado ou vencido abre; o que está em dia começa
 * recolhido. O resumo abaixo do aviso soma os obrigatórios que pedem ação.
 *
 * "Reutilizar documento" não aparece aqui: a reutilização ainda não grava, e o
 * que não conclui não é renderizado (regra 7 de interface).
 */
export function DocumentosPage() {
  const { data, error, isLoading, isError, refetch } = useDocumentos();
  const { isColetivo } = usePortalAccess();
  const { usuario } = useAuth();
  const [emEnvio, setEmEnvio] = useState<{ item: ItemDaApi; rotulo: string } | null>(null);

  const itens = useMemo(() => data?.itens ?? [], [data]);
  const grupos = useMemo(() => montarGrupos(itens, isColetivo), [itens, isColetivo]);
  const porId = useMemo(() => new Map(itens.map((item) => [idDoItem(item), item])), [itens]);
  const resumo = data ? textoDoResumo(data.contagem.pendenciasObrigatorias) : null;

  function abrirEnvio(rotulo: string) {
    return (linha: ItemDoChecklist) => {
      const item = porId.get(linha.id);
      if (item) setEmEnvio({ item, rotulo });
    };
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentos"
        description="Envie e acompanhe seus documentos. Documentos aprovados e dentro da validade não precisam ser reenviados."
      />

      <GatingBanner />

      {error?.pedeOutroCaminho ? (
        // 403: perfil sem acesso, cadastro sem vínculo ou pré-credenciado.
        // Tentar de novo não resolve; a frase vem da API. O pré-credenciado
        // ganha o atalho da tela dele (decisão F7).
        <ErrorState
          variant="forbidden"
          description={error.message}
          primaryAction={
            veDocumentacaoPreCredenciamento(usuario)
              ? {
                  label: 'Ir para Documentação do pré-credenciamento',
                  to: '/credenciado/pre-credenciamento',
                }
              : undefined
          }
        />
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar seus documentos agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div role="status" aria-live="polite">
          <CardSkeleton />
          <p className="sr-only">Carregando seus documentos.</p>
        </div>
      ) : grupos.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nenhum documento exigido"
          description="Quando houver documento obrigatório para o seu credenciamento, ele aparece aqui."
        />
      ) : (
        <>
          {resumo && (
            <Alert role="status">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{resumo}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            {grupos.map((grupo) => (
              <DocumentChecklist
                key={grupo.chave}
                title={grupo.titulo}
                subtitulo={grupo.subtitulo}
                itens={grupo.itens.map(paraOChecklist)}
                ofereceReutilizar={false}
                onEnviar={abrirEnvio(grupo.titulo)}
                recolhidoInicialmente={!grupo.temPendencia}
              />
            ))}
          </div>
        </>
      )}

      <DialogoDeEnvioDeDocumento
        item={emEnvio?.item ?? null}
        rotuloDoRecurso={emEnvio?.rotulo ?? ''}
        aoFechar={() => setEmEnvio(null)}
      />
    </div>
  );
}

export default DocumentosPage;
